import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useSearchParams } from 'react-router-dom';
import { isDatePast } from '../utils/dateUtils';
import { handleApiError } from '../utils/errorHandler';
import {
  Search,
  Eye,
  Edit,
  Trash2,
  FileText,
  Plus,
  Download,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import Layout from '../components/Layout';
import {
  fetchCustomers,
  fetchMagazines,
  fetchContentSizes,
} from '../store/slices/bookingSlice';
import api, { bookingsAPI } from '../utils/api';
import { toast } from 'react-toastify';
import { usePermissions } from '../hooks/usePermissions';

const Bookings = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { magazines, contentSizes } = useSelector(state => state.booking);
  const { canDelete, canCreateBookings } = usePermissions();

  const [filters, setFilters] = useState({
    searchTerm: searchParams.get('search') || '',
    selectedMagazine: searchParams.get('magazine') || '',
    selectedContentSize: searchParams.get('size') || '',
    selectedContentType: searchParams.get('type') || '',
    selectedStartIssue: searchParams.get('startIssue') || '',
    selectedFinishIssue: searchParams.get('finishIssue') || '',
    selectedIssue: searchParams.get('issue') || '', 
  });

  const [bookingEntries, setBookingEntries] = useState([]);

  const [contentTypes, setContentTypes] = useState([]);
  const [uniqueStartIssues, setUniqueStartIssues] = useState([]);
  const [uniqueFinishIssues, setUniqueFinishIssues] = useState([]);
  const [uniqueIssues, setUniqueIssues] = useState([]);
  const [closedIssues, setClosedIssues] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLoading, setFilterLoading] = useState(false);
  const [initialIssueSet, setInitialIssueSet] = useState(false);
  const [initialStartIssueSet, setInitialStartIssueSet] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [totalEntries, setTotalEntries] = useState(0);

  const [totalValue, setTotalValue] = useState(0);
  const [totalSize, setTotalSize] = useState(0);

  const [sortField, setSortField] = useState('customer');
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    loadInitialData();
  }, [dispatch]);

  useEffect(() => {
    if (initialIssueSet) {
      loadBookingEntries();
    }
  }, [filters, currentPage, itemsPerPage, sortField, sortDirection, initialIssueSet]);

  useEffect(() => {
    const setInitialIssue = async () => {
      if (uniqueIssues.length > 0 && schedules.length > 0 && !initialIssueSet) {
        const issueFromUrl = searchParams.get('issue');
        if (issueFromUrl !== null) {
          if (issueFromUrl === '' || uniqueIssues.includes(issueFromUrl)) {
            setFilters(prev => ({ ...prev, selectedIssue: issueFromUrl }));
            setInitialIssueSet(true);
            return;
          }
        }

        const backendCurrentIssue = await getCurrentIssueFromBackend();
        if (
          backendCurrentIssue &&
          uniqueIssues.includes(backendCurrentIssue) &&
          !isIssueClosed(backendCurrentIssue)
        ) {
          setFilters(prev => ({ ...prev, selectedIssue: backendCurrentIssue }));
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.set('issue', backendCurrentIssue);
          setSearchParams(newSearchParams, { replace: true });
          setInitialIssueSet(true);
          return;
        }

        const currentIssue = getCurrentIssue();
        if (currentIssue && uniqueIssues.includes(currentIssue)) {
          setFilters(prev => ({ ...prev, selectedIssue: currentIssue }));
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.set('issue', currentIssue);
          setSearchParams(newSearchParams, { replace: true });
          setInitialIssueSet(true);
          return;
        }
        
        if (uniqueIssues.length > 0) {
          const firstIssue = uniqueIssues[0];
          setFilters(prev => ({ ...prev, selectedIssue: firstIssue }));
          const newSearchParams = new URLSearchParams(searchParams);
          newSearchParams.set('issue', firstIssue);
          setSearchParams(newSearchParams, { replace: true });
          setInitialIssueSet(true);
        } else {
          setInitialIssueSet(true);
        }
      }
    };

    setInitialIssue();
  }, [uniqueIssues, schedules, initialIssueSet]);

  useEffect(() => {
    const setInitialStartIssue = () => {
      if (uniqueStartIssues.length > 0 && schedules.length > 0 && !initialStartIssueSet) {
        const startIssueFromUrl = searchParams.get('startIssue');
        if (startIssueFromUrl && uniqueStartIssues.includes(startIssueFromUrl)) {
          setFilters(prev => ({ ...prev, selectedStartIssue: startIssueFromUrl }));
          setInitialStartIssueSet(true);
          return;
        }

        setInitialStartIssueSet(true);
      }
    };

    setInitialStartIssue();
  }, [uniqueStartIssues, schedules, initialStartIssueSet]);

  const handleSort = field => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = field => {
    if (sortField !== field) {
      return <div className="w-4 h-4"></div>;
    }
    return sortDirection === 'asc' ? (
      <ChevronUp className="w-4 h-4" />
    ) : (
      <ChevronDown className="w-4 h-4" />
    );
  };

  const getCurrentIssue = () => {
    if (uniqueIssues.length === 0 || schedules.length === 0) {
      return null;
    }

    const now = new Date();

    const issuesWithDates = [];
    schedules.forEach(schedule => {
      if (schedule.issues && Array.isArray(schedule.issues)) {
        schedule.issues.forEach(issue => {
          issuesWithDates.push({
            name: issue.name,
            closeDate: new Date(issue.closeDate),
          });
        });
      }
    });

    for (const issue of uniqueIssues) {
      const issueData = issuesWithDates.find(i => i.name === issue);
      if (issueData && issueData.closeDate >= now) {
        return issue;
      }
    }

    return uniqueIssues[uniqueIssues.length - 1];
  };

  const loadSchedules = async () => {
    try {
      const response = await api.get('/schedules');
      setSchedules(response.data);
    } catch (error) {
      console.error('Error loading schedules:', error);
    }
  };

  const getCurrentIssueFromBackend = async () => {
    try {
      const response = await api.get('/bookings/current-issue');
      return response.data.currentIssue;
    } catch (error) {
      console.error('Error getting current issue:', error);
      return null;
    }
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        dispatch(fetchCustomers()),
        dispatch(fetchMagazines()),
        dispatch(fetchContentSizes()),
        loadContentTypes(),
        loadSchedules(),
      ]);
      await loadBookingEntries();
    } catch (error) {
      handleApiError(error, 'Failed to load bookings data');
    } finally {
      setLoading(false);
    }
  };

  const loadBookingEntries = async () => {
    try {
      setFilterLoading(true);

      const queryParams = new URLSearchParams();

      if (filters.searchTerm) queryParams.append('search', filters.searchTerm);
      if (filters.selectedMagazine) queryParams.append('magazine', filters.selectedMagazine);
      if (filters.selectedContentSize) queryParams.append('size', filters.selectedContentSize);
      if (filters.selectedContentType) queryParams.append('type', filters.selectedContentType);
      if (filters.selectedStartIssue) queryParams.append('startIssue', filters.selectedStartIssue);
      if (filters.selectedFinishIssue)
        queryParams.append('finishIssue', filters.selectedFinishIssue);
      if (filters.selectedIssue) queryParams.append('issue', filters.selectedIssue);

      queryParams.append('page', currentPage.toString());
      queryParams.append('limit', itemsPerPage.toString());

      if (sortField) {
        queryParams.append('sortField', sortField);
        queryParams.append('sortDirection', sortDirection);
      }

      const response = await api.get(`/bookings?${queryParams.toString()}`);
      const data = response.data;

      setBookingEntries(data.entries || []);
      setTotalEntries(data.totalEntries || 0);
      setTotalValue(data.totalValue || 0);
      setTotalSize(data.totalSize || 0);

      setUniqueStartIssues(data.uniqueStartIssues || []);
      setUniqueFinishIssues(data.uniqueFinishIssues || []);
      setUniqueIssues(data.uniqueIssues || []);
      setClosedIssues(data.closedIssues || []);
    } catch (error) {
      console.error('Error loading booking entries:', error);
    } finally {
      setFilterLoading(false);
    }
  };

  const isIssueClosed = issueName => {
    if (closedIssues.includes(issueName)) {
      return true;
    }

    const currentDate = new Date();
    for (const schedule of schedules) {
      const scheduleIssue = schedule.issues?.find(issue => issue.name === issueName);
      if (scheduleIssue && new Date(scheduleIssue.closeDate) < currentDate) {
        return true;
      }
    }

    return false;
  };

  const totalPages = Math.ceil(totalEntries / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalEntries);

  const handlePageChange = newPage => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handleItemsPerPageChange = newItemsPerPage => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

      if (endPage - startPage < maxVisiblePages - 1) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      if (startPage > 1) {
        pageNumbers.push(1);
        if (startPage > 2) pageNumbers.push('...');
      }

      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }

      if (endPage < totalPages) {
        if (endPage < totalPages - 1) pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }

    return pageNumbers;
  };

  const loadContentTypes = async () => {
    try {
      const response = await bookingsAPI.getAll();
      // The flat list endpoint returns { entries: [...] }
      const entries = response.data.entries || response.data || [];
      const types = new Set();
      entries.forEach(booking => {
        if (booking.contentType) types.add(booking.contentType);
      });
      setContentTypes(Array.from(types).sort());
    } catch (error) {
      console.error('Error loading content types:', error);
      setContentTypes(['Advert', 'Article', 'Puzzle', 'Advertorial', 'Front Cover', 'In-house']);
    }
  };

  const handleFilterChange = (filterName, value) => {
    const newFilters = {
      ...filters,
      [filterName]: value,
    };
    setFilters(newFilters);
    setCurrentPage(1);

    const newSearchParams = new URLSearchParams();
    Object.entries(newFilters).forEach(([key, val]) => {
      if (val) {
        const paramMap = {
          searchTerm: 'search',
          selectedMagazine: 'magazine',
          selectedContentSize: 'size',
          selectedContentType: 'type',
          selectedStartIssue: 'startIssue',
          selectedFinishIssue: 'finishIssue',
          selectedIssue: 'issue',
        };
        const paramName = paramMap[key] || key;
        newSearchParams.set(paramName, val);
      }
    });
    setSearchParams(newSearchParams);
  };

  const handleDeleteBookingRow = async (bookingId, customerName, magazineName) => {
    const confirmMessage = `Are you sure you want to delete this booking entry?\n\nCustomer: ${customerName}\nPublication: ${magazineName}\n\nThis action cannot be undone and will remove the entry from all booking lists and customer history.`;

    if (window.confirm(confirmMessage)) {
      try {
        await api.delete(`/bookings/${bookingId}`);
        toast.success('Booking entry deleted successfully');
        await loadBookingEntries();
      } catch (error) {
        console.error('Error deleting booking entry:', error);
        const errorMessage = error.response?.data?.message || 'Error deleting booking entry';
        toast.error(errorMessage);
      }
    }
  };

  const clearFilters = async () => {
    let defaultIssue = '';

    const backendCurrentIssue = await getCurrentIssueFromBackend();
    if (backendCurrentIssue && uniqueIssues.includes(backendCurrentIssue)) {
      defaultIssue = backendCurrentIssue;
    } else {
      const currentIssue = getCurrentIssue();
      if (currentIssue && uniqueIssues.includes(currentIssue)) {
        defaultIssue = currentIssue;
      } else if (uniqueIssues.length > 0) {
        defaultIssue = uniqueIssues[0];
      }
    }

    const clearedFilters = {
      searchTerm: '',
      selectedMagazine: '',
      selectedContentSize: '',
      selectedContentType: '',
      selectedStartIssue: '',
      selectedFinishIssue: '',
      selectedIssue: defaultIssue,
    };
    setFilters(clearedFilters);
    setSortField('customer');
    setSortDirection('asc');
    setCurrentPage(1);

    const newSearchParams = new URLSearchParams();
    if (defaultIssue) {
      newSearchParams.set('issue', defaultIssue);
    }
    setSearchParams(newSearchParams);
  };

  const formatCurrency = value => `£${(value || 0).toFixed(2)}`;

  const exportToCSV = async () => {
    try {
      const queryParams = new URLSearchParams();

      if (filters.searchTerm) queryParams.append('search', filters.searchTerm);
      if (filters.selectedMagazine) queryParams.append('magazine', filters.selectedMagazine);
      if (filters.selectedContentSize) queryParams.append('size', filters.selectedContentSize);
      if (filters.selectedContentType) queryParams.append('type', filters.selectedContentType);
      if (filters.selectedStartIssue) queryParams.append('startIssue', filters.selectedStartIssue);
      if (filters.selectedFinishIssue)
        queryParams.append('finishIssue', filters.selectedFinishIssue);
      if (filters.selectedIssue) queryParams.append('issue', filters.selectedIssue);

      queryParams.append('export', 'true');

      const response = await api.get(`/bookings?${queryParams.toString()}`);
      const allEntries = response.data.entries || bookingEntries;

      const headers = [
        'Customer',
        'Magazine',
        'Rate Card',
        'Type',
        'Total',
        'Start Issue',
        'Finish Issue',
      ];

      const csvData = allEntries.map(entry => [
        entry.customer?.name || '',
        entry.magazine?.name || '',
        entry.contentSize?.adSize?.name || 'N/A',
        entry.contentType || '',
        (entry.total || 0).toFixed(2),
        entry.startIssue || '',
        entry.isOngoing ? 'Ongoing' : entry.finishIssue || '',
      ]);

      const csvContent = [
        headers.join(','),
        ...csvData.map(row => row.map(field => `"${field}"`).join(',')),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute(
        'download',
        `bookings_export_${new Date().toISOString().split('T')[0]}.csv`
      );
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success('Bookings exported successfully');
    } catch (error) {
      console.error('Error exporting bookings:', error);
      toast.error('Failed to export bookings');
    }
  };

  // Shared renderer so the Issue, Start Issue and Finish Issue dropdowns all
  // label options the same way: Current / Closed, with closed issues greyed out.
  const currentIssue = getCurrentIssue();
  const renderIssueOption = issue => {
    const isCurrent = currentIssue && issue === currentIssue;
    const isClosed = isIssueClosed(issue);
    const labels = [];
    if (isCurrent) labels.push('Current');
    if (isClosed) labels.push('Closed');

    return (
      <option key={issue} value={issue} style={{ color: isClosed ? '#9ca3af' : 'inherit' }}>
        {labels.length > 0 ? `${issue} (${labels.join(', ')})` : issue}
      </option>
    );
  };

  return (
    <Layout>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="sm:flex sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Bookings</h1>
            <p className="mt-2 text-gray-600">Manage your magazine space bookings</p>
          </div>
          {canCreateBookings() && (
            <Link
              to="/bookings/new"
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md border border-transparent shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Plus className="mr-2 w-4 h-4" />
              Add New Booking
            </Link>
          )}
        </div>

        {/* Filters */}
        <div className="mt-6 bg-white rounded-lg shadow">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Filters</h3>

            <div className="mb-4 w-full">
              <div className="relative max-w-md">
                <div className="flex absolute inset-y-0 left-0 items-center pl-3 pointer-events-none">
                  <Search className="w-5 h-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={filters.searchTerm}
                  onChange={e => handleFilterChange('searchTerm', e.target.value)}
                  className="block py-2 pr-3 pl-10 w-full leading-5 placeholder-gray-500 bg-white rounded-md border border-gray-300 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Search Customers"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
            <div>
              <select
                value={filters.selectedIssue}
                onChange={e => {
                  const value = e.target.value;
                  handleFilterChange('selectedIssue', value);
                }}
                className="block px-3 py-2 w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Issues</option>
                {uniqueIssues.map(renderIssueOption)}
              </select>
            </div>

            <div>
              <select
                value={filters.selectedMagazine}
                onChange={e => handleFilterChange('selectedMagazine', e.target.value)}
                className="block px-3 py-2 w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Publications</option>
                {magazines.map(magazine => (
                  <option key={magazine._id} value={magazine._id}>
                    {magazine.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <select
                value={filters.selectedStartIssue}
                onChange={e => handleFilterChange('selectedStartIssue', e.target.value)}
                className="block px-3 py-2 w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Start Issues</option>
                {uniqueStartIssues.map(renderIssueOption)}
              </select>
            </div>

            <div>
              <select
                value={filters.selectedFinishIssue}
                onChange={e => handleFilterChange('selectedFinishIssue', e.target.value)}
                className="block px-3 py-2 w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Finish Issues</option>
                <option value="ONGOING">Ongoing</option>
                {uniqueFinishIssues.map(renderIssueOption)}
              </select>
            </div>

            <div>
              <select
                value={filters.selectedContentSize}
                onChange={e => handleFilterChange('selectedContentSize', e.target.value)}
                className="block px-3 py-2 w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Sizes</option>
                {contentSizes.map(size => (
                  <option key={size._id} value={size._id}>
                    {size.adSize?.name || 'N/A'}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <select
                value={filters.selectedContentType}
                onChange={e => handleFilterChange('selectedContentType', e.target.value)}
                className="block px-3 py-2 w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Types</option>
                {contentTypes.map(type => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
          </div>

            {Object.values(filters).some(value => value !== '') && (
              <div className="mt-4">
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <X className="mr-1 w-4 h-4" />
                  Clear Filter
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 space-y-3">
          {totalEntries > 0 && (
            <div className="grid grid-cols-1 gap-4 p-4 bg-gray-50 rounded-lg sm:grid-cols-3">
              <div className="text-center">
                <div className="text-lg font-semibold text-gray-900">{totalEntries}</div>
                <div className="text-sm text-gray-500">Total Entries</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-green-600">
                  {formatCurrency(totalValue)}
                </div>
                <div className="text-sm text-gray-500">Total Revenue</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-blue-600">{totalSize}</div>
                <div className="text-sm text-gray-500">Total Pages</div>
              </div>
            </div>
          )}

          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              {totalEntries > 0 ? (
                <>
                  Showing {startIndex}-{endIndex} of {totalEntries} booking entries
                  {Object.values(filters).some(value => value !== '') && (
                    <span> with active filters</span>
                  )}
                </>
              ) : (
                <>No booking entries found</>
              )}
            </div>

            {totalEntries > 0 && (
              <button
                onClick={exportToCSV}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <Download className="mr-2 w-4 h-4" />
                Export CSV
              </button>
            )}
          </div>
        </div>
        <div className="overflow-hidden mt-6 bg-white shadow sm:rounded-md">
          {loading ? (
            <div className="py-12 text-center">
              <div className="inline-block w-8 h-8 rounded-full border-b-2 border-blue-600 animate-spin"></div>
              <p className="mt-2 text-gray-600">Loading bookings...</p>
            </div>
          ) : bookingEntries.length === 0 ? (
            <div className="py-12 text-center">
              <FileText className="mx-auto w-12 h-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No bookings found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {Object.values(filters).some(value => value !== '')
                  ? 'Try adjusting your search or filters.'
                  : 'Get started by creating your first booking.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      className="px-6 py-3 text-xs font-medium tracking-wide text-left text-gray-500 uppercase cursor-pointer select-none hover:bg-gray-100"
                      onClick={() => handleSort('customer')}
                    >
                      <div className="flex justify-between items-center">
                        Customer
                        {getSortIcon('customer')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-xs font-medium tracking-wide text-left text-gray-500 uppercase cursor-pointer select-none hover:bg-gray-100"
                      onClick={() => handleSort('magazine')}
                    >
                      <div className="flex justify-between items-center">
                        Publication
                        {getSortIcon('magazine')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-xs font-medium tracking-wide text-left text-gray-500 uppercase cursor-pointer select-none hover:bg-gray-100"
                      onClick={() => handleSort('size')}
                    >
                      <div className="flex justify-between items-center">
                        Size
                        {getSortIcon('size')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-xs font-medium tracking-wide text-left text-gray-500 uppercase cursor-pointer select-none hover:bg-gray-100"
                      onClick={() => handleSort('type')}
                    >
                      <div className="flex justify-between items-center">
                        Type
                        {getSortIcon('type')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-xs font-medium tracking-wide text-left text-gray-500 uppercase cursor-pointer select-none hover:bg-gray-100"
                      onClick={() => handleSort('total')}
                    >
                      <div className="flex justify-between items-center">
                        Total
                        {getSortIcon('total')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-xs font-medium tracking-wide text-left text-gray-500 uppercase cursor-pointer select-none hover:bg-gray-100"
                      onClick={() => handleSort('start')}
                    >
                      <div className="flex justify-between items-center">
                        Start
                        {getSortIcon('start')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-xs font-medium tracking-wide text-left text-gray-500 uppercase cursor-pointer select-none hover:bg-gray-100"
                      onClick={() => handleSort('finish')}
                    >
                      <div className="flex justify-between items-center">
                        Finish
                        {getSortIcon('finish')}
                      </div>
                    </th>
                    <th
                      className="px-6 py-3 text-xs font-medium tracking-wide text-right text-gray-500 uppercase whitespace-nowrap"
                      style={{ minWidth: '120px' }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filterLoading ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-4 text-center text-gray-500">
                        <div className="inline-block mr-2 w-4 h-4 rounded-full border-b-2 border-blue-600 animate-spin"></div>
                        Loading...
                      </td>
                    </tr>
                  ) : (
                    bookingEntries.map(entry => {
                      const isEntryClosed = (() => {
                        if (entry.isOngoing) return false;

                        const finishIssue = entry.finishIssue;
                        if (!finishIssue) return false;

                        for (const schedule of schedules) {
                          const issue = schedule.issues?.find(i => i.name === finishIssue);
                          if (issue) {
                            return isDatePast(issue.closeDate);
                          }
                        }
                        return false;
                      })();

                      return (
                        <tr
                          key={entry.id}
                          className={`${isEntryClosed ? 'bg-red-50 opacity-75' : 'hover:bg-gray-50'}`}
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {entry.customer?.name}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{entry.magazine?.name}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {entry.contentSize?.adSize?.name || 'N/A'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{entry.contentType}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {formatCurrency(entry.total)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {(() => {
                                if (entry.monthlyArtwork && entry.monthlyArtwork.length > 0) {
                                  const firstSelected = entry.monthlyArtwork.find(artwork => artwork.isSelected);
                                  if (firstSelected && firstSelected.month) {
                                    return firstSelected.month;
                                  }
                                }
                                return entry.startIssue || 'N/A';
                              })()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {entry.isOngoing ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium text-green-800 bg-green-100 rounded-full">
                                  Ongoing
                                </span>
                              ) : (
                                <>
                                  {entry.finishIssue || '-'}
                                  {isEntryClosed && (
                                    <span className="inline-flex items-center px-2 py-0.5 ml-2 text-xs font-medium text-red-800 bg-red-100 rounded">
                                      Closed
                                    </span>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                          <td
                            className="px-6 py-4 text-sm font-medium text-right whitespace-nowrap"
                            style={{ minWidth: '120px' }}
                          >
                            <div className="flex justify-end space-x-2">
                              <Link
                                to={`/bookings/customer/${entry.customer?._id}/view`}
                                className="text-blue-600 hover:text-blue-900"
                                title="View Details"
                              >
                                <Eye className="w-4 h-4" />
                              </Link>
                              <Link
                                to={`/bookings/customer/${entry.customer?._id}`}
                                className="text-yellow-600 hover:text-yellow-900"
                                title="Edit Booking"
                              >
                                <Edit className="w-4 h-4" />
                              </Link>
                              {canDelete() && entry.bookingId && !isEntryClosed && (
                                <button
                                  onClick={() =>
                                    handleDeleteBookingRow(
                                      entry.bookingId,
                                      entry.customer?.name || 'Unknown Customer',
                                      entry.magazine?.name || 'Unknown Publication'
                                    )
                                  }
                                  className="text-red-600 hover:text-red-900"
                                  title="Delete Entry"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                              {canDelete() && entry.bookingId && isEntryClosed && (
                                <button
                                  disabled
                                  className="text-gray-400 cursor-not-allowed"
                                  title="Cannot delete closed booking entry"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          )}

          {totalEntries > 0 && (
            <div className="flex justify-between items-center px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">Show</span>
                <select
                  value={itemsPerPage}
                  onChange={e => handleItemsPerPageChange(Number(e.target.value))}
                  className="px-2 py-1 text-sm rounded border border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm text-gray-700">entries</span>
              </div>

              <div className="flex items-center space-x-2">
                {totalPages > 1 ? (
                  <>
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className={`inline-flex items-center px-3 py-2 border text-sm font-medium rounded-md ${
                        currentPage === 1
                          ? 'border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed'
                          : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      <ChevronLeft className="mr-1 w-4 h-4" />
                      Previous
                    </button>

                    <div className="flex items-center space-x-1">
                      {getPageNumbers().map((pageNum, index) => (
                        <button
                          key={index}
                          onClick={() => typeof pageNum === 'number' && handlePageChange(pageNum)}
                          disabled={pageNum === '...'}
                          className={`inline-flex items-center px-3 py-2 border text-sm font-medium rounded-md ${
                            pageNum === currentPage
                              ? 'border-blue-500 bg-blue-50 text-blue-600'
                              : pageNum === '...'
                                ? 'border-gray-300 text-gray-400 bg-gray-100 cursor-default'
                                : 'border-gray-300 text-gray-500 bg-white hover:bg-gray-50 hover:text-gray-700'
                          }`}
                        >
                          {pageNum}
                        </button>
                      ))}
                    </div>

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className={`inline-flex items-center px-3 py-2 border text-sm font-medium rounded-md ${
                        currentPage === totalPages
                          ? 'border-gray-300 text-gray-400 bg-gray-100 cursor-not-allowed'
                          : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50 hover:text-gray-900'
                      }`}
                    >
                      Next
                      <ChevronRight className="ml-1 w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <div className="text-sm text-gray-500">Page 1 of 1</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default Bookings;
